import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, of } from 'rxjs';
import { AiChatDataService, Chat, ChatUser, Message } from '../../services/ai-chat-data.service';

@Component({
  selector: 'iu-ai-chat',
  standalone: true,
  imports: [TranslateModule, FormsModule, DatePipe, AsyncPipe],
  templateUrl: './iu-ai-chat.component.html',
  styleUrl: './iu-ai-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-chat-only]': '!hasChats()',
  },
  providers: [AiChatDataService],
})
export default class IuAiChatComponent {
  private aiChatDataService = inject(AiChatDataService);
  private translateService = inject(TranslateService);

  private state = this.aiChatDataService.state;

  readonly chats = this.state.chats;
  readonly chat = this.state.chat;

  readonly message = model('');
  readonly chatListExpanded = signal(true);

  readonly welcomeMessage = toSignal(
    this.translateService.get('aiChat.welcomeMessage').pipe(
      map((messages) => {
        if (typeof messages === 'object') {
          const values = Object.values(messages);
          return values[Math.floor(Math.random() * values.length)];
        }

        return messages;
      })
    )
  );

  readonly hasChats = computed(() => this.chats().length > 1);

  toggleExpand(): void {
    this.chatListExpanded.update((v) => !v);
  }

  send(): void {
    if (this.state.loading()) {
      return;
    }

    this.state.sendMessage({
      chat: this.chat(),
      message: this.getMessage(),
    });

    this.resetInput();
  }

  newChat(): void {
    this.state.createChat();
    this.resetInput();
  }

  changeChat(id: Chat['id']): void {
    this.state.setCurrent(id);
    this.resetInput();
  }

  private resetInput(): void {
    this.message.set('');
  }

  private getMessage(): Message {
    return {
      content$: of(this.message()),
      created: new Date(),
      createdBy: ChatUser.USER,
    };
  }
}
